import type { BalanceUnitStatementEntry } from "@/openapi/generated/models";

export type StatementTuitionDiscount = {
  name: string;
  amount: number;
};

export type StatementTuitionBreakdown = {
  price: number;
  discountTotal: number;
  discounts: StatementTuitionDiscount[];
};

export type StatementEntryWithTuition = BalanceUnitStatementEntry & {
  tuitionBreakdown?: StatementTuitionBreakdown | null;
};

function readAmount(value: unknown): number | null {
  const amount = typeof value === "number" ? value : Number(value);
  return Number.isFinite(amount) ? amount : null;
}

export function readStatementTuitionBreakdown(
  value: unknown,
): StatementTuitionBreakdown | null {
  if (!value || typeof value !== "object") return null;
  const body = value as Record<string, unknown>;
  const price = readAmount(body.price);
  const discountTotal = readAmount(body.discountTotal);
  if (price == null || discountTotal == null || price <= 0 || discountTotal <= 0) {
    return null;
  }

  const discounts = Array.isArray(body.discounts)
    ? body.discounts.flatMap((line) => {
        if (!line || typeof line !== "object") return [];
        const row = line as Record<string, unknown>;
        const amount = readAmount(row.amount);
        const name = typeof row.name === "string" ? row.name.trim() : "";
        if (amount == null || amount <= 0 || name.length === 0) return [];
        return [{ name, amount }];
      })
    : [];

  return {
    price,
    discountTotal,
    discounts:
      discounts.length > 0
        ? discounts
        : [{ name: "Discount", amount: discountTotal }],
  };
}

export function statementEntryTuitionBreakdown(
  entry: BalanceUnitStatementEntry,
): StatementTuitionBreakdown | null {
  return readStatementTuitionBreakdown(
    (entry as StatementEntryWithTuition).tuitionBreakdown,
  );
}
